import { Component, NgZone } from '@angular/core';
import { Badge } from '@awesome-cordova-plugins/badge/ngx';
import { FirebaseX, IChannelOptions } from '@awesome-cordova-plugins/firebase-x/ngx';
import { ThemeDetection } from '@awesome-cordova-plugins/theme-detection/ngx';
import { Smartlook, SmartlookSetupConfig } from '@ionic-native/smartlook/ngx';
import { ActionSheetController, AlertController, ModalController, Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import 'rxjs/add/observable/combineLatest';
import { startWith } from 'rxjs/operators';

import { PUBLIC_URLS } from '@constants/miscellaneous';
import { AuthService } from '@services/auth/auth.service';
import { DeepLinksService } from '@services/deeplinks/deeplinks.service';
import { FirebaseService } from '@services/firebase/firebase.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { PushNotificationsService } from '@services/push-notifications/push-notifications.service';
import { ReadsService } from '@services/reads/reads.service';
import { StatusBarService } from '@services/status-bar/status-bar.service';
import Utils from '@util/util';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
})
export class AppComponent {
  pauseTime: Date;
  lastMessageId: string;
  swipeGesture: boolean;
  message: any;
  newMessage: boolean;
  isNarrow: boolean;
  loggedIn: boolean;
  hasMiddleColumn: boolean;
  sideMenuHidden: boolean;
  isDarkMode: boolean;
  systemDarkMode: boolean;
  isDev: boolean;

  constructor(
    private platform: Platform,
    private firebaseService: FirebaseService,
    private authService: AuthService,
    private storage: Storage,
    private pushNotifications: PushNotificationsService,
    private firebaseNative: FirebaseX,
    private readsService: ReadsService,
    private modalCtrl: ModalController,
    private alertController: AlertController,
    private actionSheetController: ActionSheetController,
    private navigationService: NavigationService,
    private statusBarService: StatusBarService,
    private ngZone: NgZone,
    private themeDetection: ThemeDetection,
    private smartlook: Smartlook,
    private badge: Badge,
    private deepLinksService: DeepLinksService,
  ) {
    this.isDev = !environment.production;
    this.checkDarkMode();
    this.initializeApp();
    this.swipeGesture = true;
    this.isNarrow = window.innerWidth < 570;
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', () => {
        this.isNarrow = window.innerWidth < 570;
      });
    });
    this.authService.sessionUpdateCallback = (loggedIn) => { this.loggedIn = loggedIn; };
    this.authService.isLoggedIn();
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.navigationService.subscribeToNavigationAndUpdateSwipe((swipeGesture) => this.swipeGesture = swipeGesture);
      if (this.platform.is('cordova')) {
        // tslint:disable-next-line: deprecation
        this.firebaseNative.onMessageReceived().pipe(startWith(null)).subscribe((message) => {
          const messageId = this.message && (this.message['gcm.message_id'] || this.message['google.message_id'] || this.message.id);
          this.newMessage = messageId !== this.lastMessageId;
          this.message = message;
          this.authService.isLoggedIn(false).then((loggedIn) => {
            if (message && message.tap && loggedIn && this.newMessage && message.page !== '') {
              this.navigationService.goTo(message.page);
            } else if (message && message.tap && this.newMessage && message.page !== '') {
              this.authService.redirectPage = message.page;
            }
            if (message && message.tap) {
              this.lastMessageId = message['gcm.message_id'] || message['google.message_id'] || message.id;
            }
          });
          this.badge.clear();
        });
        this.getPushNotificationsToken();

        // tslint:disable-next-line: deprecation
        this.platform.resume.asObservable().pipe(startWith(null)).subscribe((resume) => {
          const resumeTime = new Date();
          let diffMinutes = 10;
          if (this.pauseTime) {
            diffMinutes = Utils.differenceInMinutes(this.pauseTime, resumeTime);
          }
          this.authService.isLoggedIn(false).then(async (loggedIn) => {
            if (diffMinutes >= 5 && loggedIn && !this.authService.userInfo.preferences.permanentLogin) {
              await this.storage.set('name', this.authService.userInfo.personalInfo.nickName);
              await this.storage.set('email', this.authService.userInfo.email);
              if (this.message && this.message.tap && this.newMessage && this.message.page !== '') {
                this.authService.redirectPage = this.message.page;
              }
              await this.closeAnyModalsOrAlerts();
              this.navigationService.goToRoot('persistent-login');
              this.authService.signOut();
              this.pauseTime = null;
              this.newMessage = false;
            }
          });
        });
        this.platform.pause.subscribe(() => {
          this.pauseTime = new Date();
        });
      }
      this.firebaseService.getGeneralConstants();
      if (this.platform.is('cordova')) {
        this.deepLinksService.initializeDeepLinks();

        this.deepLinksService.onDeepLink().subscribe((data) => {
          const url = (data as any).url;
          if (this.isPublicUrl(url)) {
            this.authService.isNavigatingAwayFromLogin = true;
            this.navigateToDeepLink(url);
          } else {
            setTimeout(() => {
              this.authService.isLoggedIn(false).then(async (loggedIn) => {
                if (loggedIn) {
                  this.navigateToDeepLink(url);
                } else {
                  this.authService.redirectPage = url;
                }
              });
            }, 100); // 100ms wait for resume to signOut
          }
        });
      }
      if (this.platform.is('cordova')) {
        this.badge.clear();
      }
      this.firebaseService.setRemoteConfig();
      if (environment.production && !environment.isPartnerSite && this.platform.is('cordova')) {
        if (Math.random() < 1 / 100) { // every 100 sessions, 1 will be recorded aprox
          this.smartlook.setupAndStartRecording(new SmartlookSetupConfig(environment.smartlookProjectKey));
        }
      }
    });
  }

  getPushNotificationsToken() {
    if (this.platform.is('cordova')) {
      this.pushNotifications.getToken();
      this.firebaseNative.createChannel({
        id: 'movements',
        name: 'Movimientos',
        importance: 4,
        visibility: 1,
      } as IChannelOptions);
      this.firebaseNative.createChannel({
        id: 'asset-values',
        name: 'Variación Stocks',
        importance: 4,
        visibility: 1,
      } as IChannelOptions);
      this.firebaseNative.createChannel({
        id: 'deposit-distribution',
        name: 'Alerta distribuir depósito',
        importance: 4,
        visibility: 1,
      } as IChannelOptions);
    }
  }

  isPublicUrl(url: string) {
    return PUBLIC_URLS.some(publicUrl => url.startsWith(publicUrl));
  }

  private async navigateToDeepLink(url: string): Promise<void> {
    try {
      await this.closeAnyModalsOrAlerts();
      const success = await this.navigationService.goToRoot(url);
      if (!success) {
        await (this.navigationService as any).router.navigateByUrl(url, { replaceUrl: true });
      }
    } catch (error) {
      // Last resort: try basic navigation service
      await this.navigationService.goTo(url);
    }
  }

  async closeAnyModalsOrAlerts() {
    try {
      await this.modalCtrl.dismiss();
    } catch {
      // no modals open
    }
    try {
      await this.alertController.dismiss();
    } catch {
      // no alerts open
    }
    try {
      await this.actionSheetController.dismiss();
    } catch {
      // no action sheets open
    }
  }

  updateMiddleColumn(middleColumn: string) {
    this.hasMiddleColumn = !!middleColumn;
  }

  updateSideMenuHidden(sideMenuHidden: boolean) {
    this.sideMenuHidden = sideMenuHidden;
  }

  async checkDarkMode() {
    const mediaProperty = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemDarkMode = mediaProperty.matches;
    this.setDarkMode();

    try {
      // tslint:disable-next-line: deprecation
      mediaProperty.addListener((newMediaProperty) => {
        this.systemDarkMode = newMediaProperty.matches;
        this.setDarkMode();
      });
    } catch {}
    try {
      mediaProperty.addEventListener('change', (newMediaProperty) => {
        this.systemDarkMode = newMediaProperty.matches;
        this.setDarkMode();
      });
    } catch {}

    this.statusBarService.setAppDarkMode = async (darkMode) => {
      await this.storage.set('darkMode', darkMode);
      this.setDarkMode();
    };

    if (this.platform.is('cordova')) {
      while (true) {
        const savedDarkMode = await this.storage.get('darkMode');
        if (savedDarkMode === 'auto') {
          const systemResponse = await this.themeDetection.isDarkModeEnabled();
          if (this.systemDarkMode !== systemResponse.value) {
            this.systemDarkMode = systemResponse.value;
            this.setDarkMode();
          }
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
  }

  async setDarkMode() {
    const savedDarkMode = await this.storage.get('darkMode');
    if (savedDarkMode === 'auto' && this.platform.is('cordova')) {
      const systemResponse = await this.themeDetection.isDarkModeEnabled();
      this.systemDarkMode = systemResponse.value;
    }
    this.isDarkMode = savedDarkMode === 'auto' ? this.systemDarkMode : savedDarkMode === 'dark';
    this.statusBarService.isDarkMode = this.isDarkMode;
    this.statusBarService.darkModeSource.next(this.isDarkMode);
    this.statusBarService.setForWhitePage();
    if (this.isDarkMode) {
      document.body.classList.add('dark-mode-app-layout');
    } else {
      document.body.classList.remove('dark-mode-app-layout');
    }
  }
}
